/**
 * Workspace mode — the CLIENT-SIDE READER of a server-resolved fact.
 *
 * The workspace is either a solo practice or a chambers. That fact lives in exactly one place:
 * `Chambers.workspaceType`, delivered on the auth session as `chambers.workspaceType`.
 *
 * This hook used to DERIVE the answer by counting barrister rows loaded into the client
 * ("<=1 barrister therefore solo"), which was a second, contradicting definition: it read 'solo'
 * for a real chambers that happened to have one active barrister, it flipped while the barrister
 * list was still loading, and it depended on what the viewer was permitted to see. It also
 * invented a 'small' tier that has no meaning in the domain. All of that is gone
 * (docs/SETTINGS-MODEL.md §1, Rule #14 — one fact, one definition).
 *
 * Absent workspaceType (a session persisted before the field existed) is read as 'chambers',
 * so the client fails CLOSED until the next session refresh supplies the real value.
 */

import { useMemo } from 'react';
import { useAuthStore } from '@/features/auth/store';

export type ChambersMode = 'solo' | 'chambers';

/**
 * What the workspace is CALLED on screen.
 *
 * Until now the workspace type was never stated anywhere a signed-in user could see it: it could
 * only be inferred from things that were absent (no briefs inbox, no chambers policies), and an
 * absence is precisely what nobody notices. Support could not answer "is this account solo or
 * chambers?" without the platform console or a database query.
 *
 * The two words match the badge the platform console already uses, so operator and tenant never
 * describe the same workspace differently.
 */
export function workspaceTypeLabel(mode: ChambersMode): string {
  return mode === 'solo' ? 'Solo' : 'Chambers';
}

interface ChambersModeResult {
  /** 'solo' or 'chambers', as resolved by the server. */
  mode: ChambersMode;

  /** True if this workspace is a single barrister operating as their own practice. */
  isSolo: boolean;

  /** True if this workspace is a multi-member chambers. */
  isChambers: boolean;

  /** True if the current user is a barrister (not clerk/admin/staff). */
  isBarrister: boolean;

  /** True if the current user is a clerk. */
  isClerk: boolean;

  /** True if the current user is the chambers administrator. */
  isAdmin: boolean;

  /**
   * True if the current user may write ORGANISATION-scope settings. Mirrors the server's
   * `isWorkspaceOwner()` — the server remains the enforcer; this is for rendering only.
   */
  isWorkspaceOwner: boolean;

  /** Kept for call-site compatibility. The fact now arrives with the session, so it is never pending. */
  loading: boolean;

  /**
   * The ONE feature a solo barrister has nothing to coordinate over: the brief allocation
   * inbox (no clerk offers them briefs). Consumed by `DashboardPage`.
   *
   * This used to declare four more — master diary, availability approval, clerk dashboard,
   * workload balancing — and a `shouldSimplify` trio. Nothing read any of them. They read as
   * protection while enforcing nothing, so the next person to add a panel would have trusted a
   * guard that does not exist. Those surfaces are clerk routes, kept away from a solo barrister
   * by the nav and by the server, not by this flag. Where a flow genuinely simplifies for solo,
   * the call site reads `isSolo` directly — `Sidebar.tsx` (navigation),
   * `MatterPipelinePage.tsx` (stages), `NewMatterPage.tsx` (matter creation).
   */
  shouldHide: {
    briefsInbox: boolean;
  };
}

const BARRISTER_TYPES = ['silk', 'junior', 'pupil', 'door_tenant'];
const CLERK_TYPES = ['senior_clerk', 'junior_clerk'];

export function useChambersMode(): ChambersModeResult {
  const user = useAuthStore((s) => s.user);
  const chambers = useAuthStore((s) => s.chambers);

  return useMemo(() => {
    const memberType = user?.memberType ?? '';
    const isBarrister = BARRISTER_TYPES.includes(memberType);
    const isClerk = CLERK_TYPES.includes(memberType);
    const isAdmin = memberType === 'admin';

    const mode: ChambersMode = chambers?.workspaceType === 'solo' ? 'solo' : 'chambers';
    const isSolo = mode === 'solo';

    return {
      mode,
      isSolo,
      isChambers: !isSolo,
      isBarrister,
      isClerk,
      isAdmin,
      // Same rule as the server's isWorkspaceOwner(): the admin, or a barrister whose workspace
      // IS their own practice. A silk inside a chambers is not the owner.
      isWorkspaceOwner: isAdmin || (isSolo && isBarrister),
      loading: false,
      shouldHide: {
        briefsInbox: isSolo && isBarrister,
      },
    };
  }, [user, chambers]);
}

/** Simplified pipeline stages for a solo practice. */
export const SOLO_PIPELINE_STAGES = [
  { key: 'active', label: 'Active' },
  { key: 'listed', label: 'Hearing Listed' },
  { key: 'in_trial', label: 'In Trial' },
  { key: 'closed_won', label: 'Won' },
  { key: 'closed_settled', label: 'Settled' },
] as const;

/** Full pipeline stages for chambers. */
export const CHAMBERS_PIPELINE_STAGES = [
  { key: 'intake', label: 'Intake' },
  { key: 'allocated', label: 'Allocated' },
  { key: 'active', label: 'Active' },
  { key: 'listed', label: 'Hearing Listed' },
  { key: 'in_trial', label: 'In Trial' },
  { key: 'judgment_reserved', label: 'Judgment' },
  { key: 'closed_won', label: 'Won' },
  { key: 'closed_settled', label: 'Settled' },
  { key: 'closed_lost', label: 'Lost' },
] as const;
